import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Table } from "antd";
import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";
import "./stylesDataTable.css";
import {
  getDataTableStorageKey,
  getInMemoryColumnsState,
  readPersistedColumnsState,
  reconcileColumnsWithState,
  writePersistedColumnsState,
} from "./dataTablePersistence";
import EllipsisCell from "./EllipsisCell";

const FALLBACK_COLUMN_WIDTH = 160;
const MIN_AUTO_SCROLL_X = 800;

// When a table has a vertical scroll, its body grows to fill the space between the
// table and the bottom of the viewport instead of stopping at a fixed pixel height.
// The bottom gap reserves room for page padding + any pagination rendered below the table.
const FILL_HEIGHT_MIN = 240;
const FILL_HEIGHT_BOTTOM_GAP = 88;

export const DATA_TABLE_SCROLL_PRESETS = {
  compact: { x: 1000, y: 400 },
  default: { x: 1300, y: 400 },
  wide: { x: 1500, y: 500 },
  ultraWide: { x: 2000, y: 500 },
  xCompact: { x: 1000 },
  xWide: { x: 1500 },
  xUltraWide: { x: 2500 },
  // `y` is a numeric placeholder only — it enables fillHeight (see shouldFillHeight)
  // and is immediately replaced by the measured remaining-viewport height.
  verticalOnly: { y: 500 },
};

/** Default Table `sortDirections`: first click is descending (Ant Design default is ascend-first). */
export const DATA_TABLE_DEFAULT_SORT_DIRECTIONS = ["descend", "ascend"];

const ResizableDraggableTitle = ({
  onResize,
  onResizeStop,
  width,
  index,
  moveColumn,
  dragOverIndex,
  setDragOverIndex,
  onDragStartColumn,
  onDragEndColumn,
  children,
  className,
  ...restProps
}) => {
  const columnWidth = width || FALLBACK_COLUMN_WIDTH;

  // Ant Design attaches sort onClick/onKeyDown/tabIndex to `<th>`; keep them on the title+sorter area only, not `::`.
  const {
    onClick: sortOnClick,
    onKeyDown: sortOnKeyDown,
    tabIndex: headerTabIndex,
    ...thProps
  } = restProps || {};

  const handleDrop = (event) => {
    event.preventDefault();
    const dragIndex = Number(event.dataTransfer.getData("text/plain"));

    if (Number.isNaN(dragIndex) || dragIndex === index) {
      setDragOverIndex(null);
      return;
    }

    moveColumn(dragIndex, index);
    setDragOverIndex(null);
  };

  const isDropTarget = dragOverIndex === index;

  return (
    <Resizable
      width={columnWidth}
      height={0}
      handle={
        <span
          className="data-table-resize-handle"
          onClick={(event) => event.stopPropagation()}
        />
      }
      onResize={onResize}
      onResizeStop={onResizeStop}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th
        {...thProps}
        className={`data-table-header-cell ${isDropTarget ? "data-table-header-cell--drop-target" : ""} ${className || ""}`}
        onDragOver={(event) => {
          event.preventDefault();
          setDragOverIndex(index);
        }}
        onDrop={handleDrop}
      >
        <div className="data-table-header-inner">
          <span
            className="data-table-drag-handle"
            draggable
            aria-hidden="true"
            tabIndex={-1}
            onDragStart={(event) => {
              event.dataTransfer.setData("text/plain", String(index));
              onDragStartColumn(index);
            }}
            onDragEnd={onDragEndColumn}
            onClick={(event) => event.stopPropagation()}
            onClickCapture={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            onMouseDownCapture={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onPointerDownCapture={(event) => event.stopPropagation()}
          >
            ::
          </span>
          <span
            className="data-table-header-sort-zone"
            onClick={sortOnClick}
            onKeyDown={sortOnKeyDown}
            tabIndex={headerTabIndex}
          >
            {children}
          </span>
        </div>
      </th>
    </Resizable>
  );
};

const DataTable = ({
  dataSource,
  data,
  columns = [],
  components,
  id,
  className,
  rowKey,
  scroll,
  scrollPreset,
  scrollOverrides,
  fillHeight,
  fillHeightOffset = FILL_HEIGHT_BOTTOM_GAP,
  fillHeightMin = FILL_HEIGHT_MIN,
  ...props
}) => {
  const dataTestId = props["data-testid"];
  const containerRef = useRef(null);
  const [measuredFillHeight, setMeasuredFillHeight] = useState(null);
  const normalizedColumns = useMemo(
    () =>
      columns.map((column, index) => {
        const key = column?.key || column?.dataIndex || `column-${index}`;

        // `ellipsis: false` opts the column out — for action buttons, chips,
        // and other widgets that shouldn't be forced onto one line.
        if (column?.ellipsis === false) {
          return { ...column, key };
        }

        const originalRender = column?.render;
        const wrappedRender = (value, record, rowIndex) => {
          const rendered = originalRender
            ? originalRender(value, record, rowIndex)
            : value;
          // Use the raw primitive when available so JSX-wrapped values
          // (badges, formatted numbers) get a clean text tooltip.
          const tooltipText =
            typeof value === "string" || typeof value === "number"
              ? String(value)
              : rendered;
          return <EllipsisCell title={tooltipText}>{rendered}</EllipsisCell>;
        };

        // showTitle: false kills Ant's native browser title — our Tooltip handles hover.
        const ellipsisProp =
          column?.ellipsis && typeof column.ellipsis === "object"
            ? { ...column.ellipsis, showTitle: false }
            : { showTitle: false };

        return {
          ...column,
          key,
          ellipsis: ellipsisProp,
          render: wrappedRender,
        };
      }),
    [columns]
  );

  const storageKey = useMemo(() => {
    return getDataTableStorageKey({
      tableIdentity: {
        id,
        dataTestId,
        className,
        rowKey,
      },
      columns: normalizedColumns,
    });
  }, [className, dataTestId, id, normalizedColumns, rowKey]);

  const persistedState = useMemo(() => {
    return readPersistedColumnsState(storageKey);
  }, [storageKey]);

  const [orderedColumns, setOrderedColumns] = useState(() => {
    const fallbackState = getInMemoryColumnsState(normalizedColumns);
    const nextState = persistedState || fallbackState;
    return reconcileColumnsWithState(normalizedColumns, nextState);
  });
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    setOrderedColumns((prevColumns) => {
      const inMemoryState = getInMemoryColumnsState(prevColumns);
      const nextState = persistedState || inMemoryState;
      return reconcileColumnsWithState(normalizedColumns, nextState);
    });
  }, [normalizedColumns, persistedState]);

  // Sync ref so resize/reorder callbacks can read the latest state synchronously.
  const orderedColumnsRef = useRef(orderedColumns);
  useEffect(() => {
    orderedColumnsRef.current = orderedColumns;
  }, [orderedColumns]);

  // Coalesce live resize ticks to one state commit per frame — react-resizable
  // otherwise fires ~60 setStates/sec and re-renders the whole table each time.
  const pendingResizeRef = useRef(new Map());
  const resizeFrameRef = useRef(null);

  const flushPendingResizes = useCallback(() => {
    resizeFrameRef.current = null;
    const pending = pendingResizeRef.current;
    if (pending.size === 0) return;
    pendingResizeRef.current = new Map();
    setOrderedColumns((prevColumns) => {
      const nextColumns = [...prevColumns];
      pending.forEach((width, index) => {
        if (nextColumns[index]) {
          nextColumns[index] = { ...nextColumns[index], width };
        }
      });
      return nextColumns;
    });
  }, []);

  useEffect(
    () => () => {
      if (resizeFrameRef.current != null && typeof window !== "undefined") {
        window.cancelAnimationFrame(resizeFrameRef.current);
      }
    },
    []
  );

  const handleResize = useCallback(
    (index) =>
      (_event, { size }) => {
        pendingResizeRef.current.set(index, size.width);
        if (resizeFrameRef.current == null && typeof window !== "undefined") {
          resizeFrameRef.current = window.requestAnimationFrame(flushPendingResizes);
        }
      },
    [flushPendingResizes]
  );

  // Persist once per gesture: flush any pending rAF, then write the final width.
  const handleResizeStop = useCallback(
    (index) =>
      (_event, { size }) => {
        pendingResizeRef.current.set(index, size.width);
        if (resizeFrameRef.current != null && typeof window !== "undefined") {
          window.cancelAnimationFrame(resizeFrameRef.current);
          resizeFrameRef.current = null;
        }
        flushPendingResizes();
        const persistedColumns = orderedColumnsRef.current.map((col, i) =>
          i === index ? { ...col, width: size.width } : col
        );
        writePersistedColumnsState(storageKey, persistedColumns);
      },
    [flushPendingResizes, storageKey]
  );

  const moveColumn = useCallback(
    (dragIndex, hoverIndex) => {
      const nextColumns = [...orderedColumnsRef.current];
      const [draggedColumn] = nextColumns.splice(dragIndex, 1);
      nextColumns.splice(hoverIndex, 0, draggedColumn);
      setOrderedColumns(nextColumns);
      writePersistedColumnsState(storageKey, nextColumns);
    },
    [storageKey]
  );

  const columnsWithBehaviors = useMemo(
    () =>
      orderedColumns.map((column, index) => ({
        ...column,
        onHeaderCell: (col) => ({
          ...(typeof column.onHeaderCell === "function" ? column.onHeaderCell(col) : {}),
          width: column.width || FALLBACK_COLUMN_WIDTH,
          onResize: handleResize(index),
          onResizeStop: handleResizeStop(index),
          index,
          moveColumn,
          dragOverIndex,
          setDragOverIndex,
          onDragStartColumn: () => setDragOverIndex(null),
          onDragEndColumn: () => setDragOverIndex(null),
        }),
      })),
    [orderedColumns, handleResize, handleResizeStop, moveColumn, dragOverIndex]
  );

  const mergedComponents = useMemo(
    () => ({
      ...components,
      header: {
        ...components?.header,
        cell: ResizableDraggableTitle,
      },
    }),
    [components]
  );

  const resolvedScroll = useMemo(() => {
    const presetScroll = scrollPreset ? DATA_TABLE_SCROLL_PRESETS[scrollPreset] : null;
    const baseScroll = scroll || presetScroll;

    const autoScrollX = columnsWithBehaviors.reduce((totalWidth, column) => {
      const columnWidth =
        typeof column?.width === "number" ? column.width : FALLBACK_COLUMN_WIDTH;
      return totalWidth + columnWidth;
    }, 0);
    const defaultX = Math.max(autoScrollX, MIN_AUTO_SCROLL_X);

    if (baseScroll || scrollOverrides) {
      const merged = {
        ...(baseScroll || {}),
        ...(scrollOverrides || {}),
      };
      // With scroll.y, Ant Design needs a concrete scroll.x for the fixed-header layout.
      // Only `y` (scrollOverrides) or unreliable values like "max-content" drop horizontal scroll.
      if (
        merged.y != null &&
        (merged.x === undefined ||
          merged.x === null ||
          merged.x === "max-content")
      ) {
        merged.x = defaultX;
      }
      return merged;
    }

    return {
      x: defaultX,
    };
  }, [columnsWithBehaviors, scroll, scrollPreset, scrollOverrides]);

  const baseScrollY = resolvedScroll?.y;
  // Fill the viewport whenever the table has a numeric vertical scroll (the presets),
  // unless a caller explicitly opts out with fillHeight={false} or supplies its own
  // (e.g. string `calc(...)`) height. Callers can force it on with fillHeight.
  const shouldFillHeight =
    fillHeight === true ||
    (fillHeight !== false && typeof baseScrollY === "number");

  useLayoutEffect(() => {
    if (!shouldFillHeight || typeof window === "undefined") {
      setMeasuredFillHeight(null);
      return undefined;
    }

    let frame = null;
    const measure = () => {
      frame = null;
      const node = containerRef.current;
      if (!node) return;
      // Measure from the scroll body (excludes the sticky header) so `scroll.y`
      // maps directly to remaining viewport space.
      const body = node.querySelector(".ant-table-body");
      const anchor = body || node;
      const top = anchor.getBoundingClientRect().top;
      const next = Math.max(
        fillHeightMin,
        Math.round(window.innerHeight - top - fillHeightOffset)
      );
      setMeasuredFillHeight((prev) => (prev === next ? prev : next));
    };
    const schedule = () => {
      if (frame == null) frame = window.requestAnimationFrame(measure);
    };

    // Measure synchronously on mount so the first paint uses the real height
    // (no flash from the preset fallback), then debounce subsequent updates.
    measure();
    window.addEventListener("resize", schedule);
    let observer = null;
    // Observe the table container only (its own reflow, e.g. rows added by polling).
    // Viewport changes are covered by the resize listener above. This avoids the
    // continuous churn of observing document.body on live-updating dashboards.
    if (typeof ResizeObserver !== "undefined" && containerRef.current) {
      try {
        const candidate = new ResizeObserver(schedule);
        if (typeof candidate.observe === "function") {
          candidate.observe(containerRef.current);
          observer = candidate;
        }
      } catch {
        observer = null;
      }
    }
    return () => {
      if (frame != null) window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", schedule);
      if (observer && typeof observer.disconnect === "function") {
        observer.disconnect();
      }
    };
  }, [shouldFillHeight, fillHeightMin, fillHeightOffset, baseScrollY]);

  const finalScroll = useMemo(() => {
    if (!shouldFillHeight || measuredFillHeight == null) return resolvedScroll;
    return { ...resolvedScroll, y: measuredFillHeight };
  }, [resolvedScroll, shouldFillHeight, measuredFillHeight]);

  return (
    <div ref={containerRef} className="data-table-fill">
      <Table
        id={id}
        rowKey={rowKey}
        dataSource={dataSource ?? data ?? []}
        columns={columnsWithBehaviors}
        components={mergedComponents}
        scroll={finalScroll}
        sortDirections={DATA_TABLE_DEFAULT_SORT_DIRECTIONS}
        className={["data-table-shared", className].filter(Boolean).join(" ")}
        {...props}
      />
    </div>
  );
};

export default DataTable;
